/* Milo — in-cart canvas size swap (feature #04).
   Add-then-remove variant swap that preserves the portrait (all _portrait_* props),
   recomputes _hd_included, and drops a now-redundant paid HD line on upgrade.
   Click-only control (button + listbox) so it never emits a `change` into <cart-items>
   (the #02b landmine). One re-render at the end; rollback-on-failure so the original is
   never lost and never doubled. */
(function () {
  if (customElements.get('milo-size-swap')) return;

  customElements.define('milo-size-swap', class MiloSizeSwap extends HTMLElement {
    connectedCallback() {
      // Bind to `this`; look up children lazily on interaction (custom-element/parse race, see #02b).
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', function (e) { e.stopPropagation(); }, true); // belt-and-suspenders
      this._onDocClick = this._onDocClick.bind(this);
      this._onKey = this._onKey.bind(this);
    }
    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick, true);
    }

    get button() { return this.querySelector('.milo-size-swap__button'); }
    get menu()   { return this.querySelector('.milo-size-swap__menu'); }

    onClick(event) {
      var option = event.target.closest('.milo-size-swap__option');
      if (option && this.contains(option)) {
        event.preventDefault(); event.stopPropagation();
        this.selectOption(option); return;
      }
      if (event.target.closest('.milo-size-swap__button')) {
        event.preventDefault(); event.stopPropagation();
        this.toggleMenu();
      }
    }

    toggleMenu() {
      if (this.hasAttribute('data-busy')) return;
      if (this.menu.hasAttribute('hidden')) this.openMenu(); else this.closeMenu();
    }
    openMenu() {
      this.menu.removeAttribute('hidden');
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this._onDocClick, true);
      this.addEventListener('keydown', this._onKey);
    }
    closeMenu() {
      this.menu.setAttribute('hidden', '');
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this._onDocClick, true);
    }
    _onDocClick(e) { if (!this.contains(e.target)) this.closeMenu(); }
    _onKey(e) { if (e.key === 'Escape') { this.closeMenu(); this.button.focus(); } }

    selectOption(option) {
      this.closeMenu();
      if (option.getAttribute('data-unavailable') === 'true') return;
      var newVariantId = option.getAttribute('data-variant-id');
      if (newVariantId === this.getAttribute('data-current-variant-id')) return; // no-op: same size
      this.swap(newVariantId, option.getAttribute('data-variant-title'),
                option.getAttribute('data-hd-included') === 'true');
    }

    readProps() {
      // {{ item.properties | json }} serializes Shopify's line-item properties as an array of
      // [key, value] pairs, NOT a plain object — Object.keys() on that array yields "0","1","2"…
      // (never "_"-prefixed), so the old direct-object read silently dropped every property.
      var el = this.querySelector('.milo-size-swap__props');
      var props = {};
      try {
        var raw = JSON.parse(el.textContent);
        if (Array.isArray(raw)) {
          raw.forEach(function (pair) { if (pair && pair.length === 2) props[pair[0]] = pair[1]; });
        } else if (raw && typeof raw === 'object') {
          props = raw;
        }
      } catch (_) {}
      return props;
    }
    addUrl()    { return (window.theme && theme.routes && theme.routes.cart_add_url)    || '/cart/add.js'; }
    changeUrl() { return (window.theme && theme.routes && theme.routes.cart_change_url) || '/cart/change.js'; }
    updateUrl() { return (window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js'; }
    setBusy(on) { if (on) this.setAttribute('data-busy', ''); else this.removeAttribute('data-busy'); }
    fail(cartItems, lineKey, msg) {
      this.setBusy(false);
      if (cartItems && cartItems.disableLoading) cartItems.disableLoading(lineKey);
      if (msg) console.error('[milo-size-swap]', msg);
    }

    async swap(newVariantId, newTitle, newHdIncluded) {
      var lineKey = this.getAttribute('data-line-key');
      var qty     = parseInt(this.getAttribute('data-quantity'), 10) || 1;
      var hdKey   = this.getAttribute('data-hd-key');        // paid HD line for this canvas, if any
      var cartItems = this.closest('cart-items');

      // Carry EVERY _-prefixed property, then override size + hd_included (future _portrait_* props
      // carry automatically — no per-field enumeration to keep in sync).
      var src = this.readProps(), props = {};
      Object.keys(src).forEach(function (k) { if (k.charAt(0) === '_') props[k] = src[k]; });
      props['_portrait_size'] = newTitle;
      props['_hd_included']   = newHdIncluded ? 'yes' : 'no';

      this.setBusy(true);
      if (cartItems && cartItems.enableLoading) cartItems.enableLoading(lineKey);
      this._abort && this._abort.abort();
      this._abort = new AbortController();

      // 1) ADD new variant — no `sections` (don't paint the transient two-line state).
      var added;
      try {
        added = await fetch(this.addUrl(), Object.assign({}, theme.utils.fetchConfig(), {
          body: JSON.stringify({ id: newVariantId, quantity: qty, properties: props }),
          signal: this._abort.signal
        })).then(function (r) { return r.json(); });
        if (added.status) throw new Error(added.description || 'add failed');
      } catch (err) {
        if (err.name === 'AbortError') return;
        return this.fail(cartItems, lineKey, err.message);
      }
      var newKey = added.key;

      // 2) REMOVE old canvas (+ paid HD line when upgrading to an HD-included size) in ONE atomic
      //    /cart/update.js call. Either both removals apply or neither does — no partial-commit
      //    window, so a mid-sequence failure can never strand a paid HD line without its canvas.
      var updates = {};
      updates[lineKey] = 0;
      if (newHdIncluded && hdKey) updates[hdKey] = 0;
      var fromVariantId = this.getAttribute('data-current-variant-id');
      try {
        var sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections',
          { bubbles: true, detail: { sections: sections } }));
        var res = await fetch(this.updateUrl(), Object.assign({}, theme.utils.fetchConfig(), {
          body: JSON.stringify({ updates: updates, sections: sections })
        })).then(function (r) { return r.json(); });
        if (res.status) throw new Error('update failed');
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'milo-size-swap', cart: res });
        if (window.dataLayer) dataLayer.push({ event: 'milo_cart_size_changed', from: fromVariantId, to: newVariantId });
      } catch (err) {
        // The single update failed → NO removal committed → removing just the added line restores the
        // exact original cart (canvas AND paid HD intact). Truly never lost, never doubled.
        try {
          var s2 = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections',
            { bubbles: true, detail: { sections: s2 } }));
          var r2 = await fetch(this.changeUrl(), Object.assign({}, theme.utils.fetchConfig(), {
            body: JSON.stringify({ id: newKey, quantity: 0, sections: s2 })
          })).then(function (r) { return r.json(); });
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'milo-size-swap', cart: r2 });
        } catch (_) {}
        return this.fail(cartItems, lineKey, err.message);
      }
      // Success: the re-render replaced this element — nothing to reset.
    }
  });
})();
